import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { Heading, Text } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { useState } from 'react';

import { ContactModal } from './ContactModal';

export interface Props {
  companyName: string;
  description: string;
  logoUrl?: string;
  regions: string[];
  website?: string;
  email?: string;
  logoBackgroundUrl?: string;
}

export const PartnerCard = ({ companyName, description, regions, website, email, logoBackgroundUrl }: Props) => {
  const [isContactCompanyModalOpen, setIsContactCompanyModalOpen] = useState(false);

  const handleContactCompanyClick = () => setIsContactCompanyModalOpen(true);
  const handleCloseModal = () => setIsContactCompanyModalOpen(false);

  return (
    <>
      <div css={cardContainerStyles} data-testid="company-card" onClick={handleContactCompanyClick}>
        <div css={box1}>
          <Flex alignItems="center" justifyContent="center">
            {logoBackgroundUrl && <img src={logoBackgroundUrl} alt={`${companyName} logo`} css={logoImageStyles} />}
          </Flex>
        </div>
        <div css={box2}>
          <div css={contentContainerStyles}>
            <Heading level={4}>{companyName}</Heading>
            <Text color={token('color.text.subtle')} size="small">
              {description}
            </Text>
            <div css={regionContainerStyles}>
              <Text color={token('color.text')} size="regular">
                Region:
              </Text>
              <Flex gap="space.100" alignItems="center" wrap="wrap">
                {regions.map((region) => (
                  <div key={region} css={regionBadgeStyles} data-testid={`region-${region}`}>
                    {region}
                  </div>
                ))}
              </Flex>
            </div>
          </div>
        </div>
      </div>
      <ContactModal
        isOpen={isContactCompanyModalOpen}
        onClose={handleCloseModal}
        companyName={companyName}
        description={description}
        email={email}
        website={website}
        logoBackgroundUrl={logoBackgroundUrl}
      />
    </>
  );
};

const cardContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flex: '1 0 0',
  borderRadius: '0.5rem',
  boxShadow: '0 0 1px 0 rgba(9, 30, 66, 0.31), 0 1px 1px 0 rgba(9, 30, 66, 0.25)',
  cursor: 'pointer',
});

const box1 = css({
  display: 'flex',
  maxHeight: '6rem',
  margin: '1rem 0.5rem',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: token('space.200'),
  alignSelf: 'stretch',
});

const box2 = css({
  display: 'flex',
  padding: '1rem 1.5rem 1.5rem 1.5rem',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: token('space.300'),
  flex: '1 1 auto',
  alignSelf: 'stretch',
});

const logoImageStyles = css({
  objectFit: 'contain',
  width: '100%',
});

const regionBadgeStyles = css({
  borderRadius: '4px',
  border: `1px solid ${token('color.border')}`,
  background: `${token('color.background.input')}`,
  display: 'flex',
  padding: `${token('space.075')} ${token('space.100')}`,
  justifyContent: 'center',
  alignItems: 'center',
});

const contentContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.100'),
  height: '100%',
});

const regionContainerStyles = css({
  display: 'flex',
  gap: token('space.100'),
  alignItems: 'center',
  marginTop: 'auto',
});
